<script>
    import { slide } from 'svelte/transition';
    import { Tooltip } from 'sveltestrap';
    import { tooltip } from "@svelte-plugins/tooltips";

    import { getTriggerDatesTooltip, isDateInTriggerDates } from '../../trigger-date.js';
    import { formatDate, formatDateTime, dateDescriptionStringAddExtraDescriptor } from '../../date-time.js';
    import { vptServer, token, inmateDrawerOpen, getTooltipDisplayOptions } from '../../stores.js';
    import { referenceDataCellStatusList, referenceDataRegimeLevelList, getReferenceDataSurveyQuestionDescription, 
        getReferenceDataSurveyAnswerGroupingColour, getReferenceDataSurveyAnswerGroupingDescription, 
        getReferenceDataSurveyAnswerColour, getReferenceDataSurveyAnswerDescription, getReferenceDataSiteName, 
        getReferenceDataItemFromList } from "../../reference-data.js";

    import AimTooltip from '../../component/AimTooltip.svelte'

    export let id;
    export let todaysConfig;

    let history = [];
    let searchCompleted = false;
    let selectedAnswerGroup;

    $: {
        if (todaysConfig) {
            history = null;
            getSurveyHistory();
        }
    }

    async function getSurveyHistory() {
        searchCompleted = false;
        history = [];
        
        await fetch(`${$vptServer}/inmate/${id}/survey/list`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            let firstCard;
            data.forEach((s,index,arr) => {
                if (firstCard == null) firstCard = s.surveyHeaderUrn;
                arr[index] = {...s,
                        surveyAnswers: [splitSurveyAnswers(true, s.surveyAnswers), splitSurveyAnswers(false, s.surveyAnswers)],
                        surveyAnswerGroups: groupSurveyAnswers(s.surveyAnswers),
                        active: firstCard == s.surveyHeaderUrn ? true : false};
            });
            history = data;
        })
        .catch(error => {
            console.error("Error getting survey history");
            console.error(error);
            return;
        });
        searchCompleted = true;
    }

    function splitSurveyAnswers(firstColumn, surveyAnswers) {
        let colLength = surveyAnswers.length / 2;
        colLength += surveyAnswers.length % 2;

        if (firstColumn) {
            return surveyAnswers.slice(0, colLength);
        } else {
            return surveyAnswers.slice(colLength, surveyAnswers.length + 1);
        }
    }

    function groupSurveyAnswers(surveyAnswers) {
        let totalAnswers = 0;
        let surveyAnswerMap = new Map();

        surveyAnswers.forEach((surveyAnswer) => {
            if (surveyAnswerMap.has(surveyAnswer.grouping)) {
                surveyAnswerMap.set(surveyAnswer.grouping, (surveyAnswerMap.get(surveyAnswer.grouping) + 1));
            } else {
                surveyAnswerMap.set(surveyAnswer.grouping, 1);
            }
            totalAnswers++;
        });
        let surveyAnswerMapSorted = new Map([...surveyAnswerMap].sort());

        let surveyAnswerGroups = [];
        surveyAnswerMapSorted.forEach((v, k) => {
            surveyAnswerGroups.push({grouping : k, total : v, percentage : Math.round((v / totalAnswers) * 100)});
        });

        return surveyAnswerGroups;
    }
</script>

<style>
    .container-fluid {
        padding-top: 163px;
        min-width: 1100px;
    }

    .container-fluid {
        padding-right: 96px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }

    .container-fluid-short {
        padding-right: 270px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }
    
    .card {
        margin-top: 5px;
    }

    .card-head .target-indicator {
        position: absolute;
        height: 16px;
        width: 16px;
        left: 8px;
    }

    .card-head .immediate-target-answers, .card-head .card-progress-container {
        display: inline-block;
    }

    .card-head .immediate-target-answers {
        margin-right: 5px;
        vertical-align: middle;
    }

    .badge {
        margin-right: 5px;
    }

    .survey-date {
        margin-left: 5px;
        margin-right: 5px;
    }

    .td-pin img {
        height: 15px;
    }

    .selected-row {
        font-weight: 600;
    }

    .hidden-row {
        filter: opacity(30%);
    }

    .survey-details {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding-top: 13px;
        padding-bottom: 3px;
        margin-top: 8px;
        font-size: 14px;
        color: #7e8299;
        border-top: 1px solid #f5f5f5
    }

    .survey-detail {
        display: flex;
        align-items: center;
    }

    .survey-detail img {
        padding-bottom: 2px;
        padding-left: 4px;
    }
</style>

<div class="container-fluid" class:container-fluid-short={$inmateDrawerOpen}>
    {#if searchCompleted}
        {#if history.length > 0}
            {#each history as survey}
                <div class="row">
                    <div class="col-sm-12">
                        <div class="card">
                            <button on:click="{() => {survey.active = !survey.active; survey = survey;}}"
                                class="button-reset focus-outline card-head" class:collapsed={!survey.active}>

                                {#if survey.indicatorTargeted}
                                    <img id="target-indicator-{survey.surveyHeaderUrn}" class="target-indicator" src="/assets/target-indicator.svg" alt="{todaysConfig.inmateLabelName} was targeted on this survey" />
                                    <Tooltip target="target-indicator-{survey.surveyHeaderUrn}" placement="bottom">{todaysConfig.inmateLabelName} was targeted on this survey.</Tooltip>
                                {/if}

                                {#if survey.level == 'H'}
                                    <span id="survey-level-badge-{survey.surveyHeaderUrn}" class="badge badge-level badge-danger">High</span>
                                {/if}
                                {#if survey.level == 'M'}
                                    <span id="survey-level-badge-{survey.surveyHeaderUrn}" class="badge badge-level badge-warning">Medium</span>
                                {/if}
                                {#if survey.level == 'L'}
                                    <span id="survey-level-badge-{survey.surveyHeaderUrn}" class="badge badge-level badge-success">Low</span>
                                {/if}
                                <Tooltip target="survey-level-badge-{survey.surveyHeaderUrn}" placement="bottom">
                                    Total Weighted Score: {survey.weightedPercentage}%
                                </Tooltip>

                                {#if survey.indicatorTrend == 'U'}
                                    <img id="inmate-trend-increase-{survey.surveyHeaderUrn}" src="/assets/arrow/arrow-up-danger.svg" class="trend-arrow" alt="Combined survey responses are more negative than their previous survey" />
                                    <Tooltip target="inmate-trend-increase-{survey.surveyHeaderUrn}" placement="bottom">Combined survey responses are more negative than their previous survey</Tooltip>
                                {:else if survey.indicatorTrend == 'D'}
                                    <img id="inmate-trend-decrease-{survey.surveyHeaderUrn}" src="/assets/arrow/arrow-down-success.svg" class="trend-arrow" alt="Combined survey responses are more positive than their previous survey" />
                                    <Tooltip target="inmate-trend-decrease-{survey.surveyHeaderUrn}" placement="bottom">Combined survey responses are more positive than their previous survey</Tooltip>
                                {/if}

                                <span class="survey-date" id="survey-date-{survey.surveyHeaderUrn}">
                                    {dateDescriptionStringAddExtraDescriptor(survey.dateSurveyDescription, true)}
                                </span>
                                <Tooltip target="survey-date-{survey.surveyHeaderUrn}" placement="bottom">
                                    Completed on {formatDateTime(survey.dateSurvey)}
                                </Tooltip>

                                {#if survey.triggerDateList && survey.triggerDateList.length > 0}
                                    <span class="td-pin"
                                        use:tooltip={{
                                        content: {
                                            component: AimTooltip ,
                                            props: getTriggerDatesTooltip(survey.triggerDateList)
                                        },
                                        ...getTooltipDisplayOptions({ align: "left", maxWidth: 500 })
                                    }}>
                                        {#if isDateInTriggerDates(survey.dateSurvey, survey.triggerDateList)}
                                            <img src="/assets/pin-danger.svg" alt="pin" />
                                        {:else}
                                            <img src="/assets/pin-fill.svg" alt="pin" />
                                        {/if}
                                    </span>
                                {/if}

                                <div class="ml-auto">
                                    <div class="immediate-target-answers">
                                        {#each survey.surveyAnswers as surveyAnswers}
                                            {#each surveyAnswers as surveyAnswer}
                                                {#if surveyAnswer.immediateTargeted}
                                                    <div class="badge-notched-icon-group">
                                                        <div class="badge-notched-icon badge-notched-icon-danger">
                                                            <img src="/assets/target-indicator-inverse.svg" alt="Immediate Targeting" />
                                                        </div>
                                                        <span class="badge badge-notched">
                                                            {getReferenceDataSurveyQuestionDescription(survey.surveyUrn, surveyAnswer.questionNumber, true)}
                                                        </span>
                                                    </div>
                                                {/if}
                                            {/each}
                                        {/each}
                                    </div>

                                    <div class="card-progress-container">
                                        <div class="progress">
                                            {#each survey.surveyAnswerGroups as surveyAnswer}
                                                <button class="button-reset focus-outline progress-bar" id="answers-{survey.surveyHeaderUrn}-{surveyAnswer.grouping}"
                                                        style="background-color:{getReferenceDataSurveyAnswerGroupingColour(survey.surveyUrn, surveyAnswer.grouping)}; width: {surveyAnswer.percentage}%"
                                                        on:click|stopPropagation={() => (selectedAnswerGroup === surveyAnswer.grouping) ? selectedAnswerGroup = "" : selectedAnswerGroup = surveyAnswer.grouping}>
                                                    <span>{surveyAnswer.total}</span>
                                                </button>
                                                <Tooltip target="answers-{survey.surveyHeaderUrn}-{surveyAnswer.grouping}" placement="bottom">
                                                    {getReferenceDataSurveyAnswerGroupingDescription(survey.surveyUrn, surveyAnswer.grouping, false)} ({surveyAnswer.percentage}%)
                                                </Tooltip>
                                            {/each}
                                        </div>
                                    </div>
                                </div>

                                {#if survey.active}
                                    <img class="card-collapse-control" src="/assets/arrow/chevron-down.svg" alt="Collapse Card" />
                                {:else}
                                    <img class="card-collapse-control" src="/assets/arrow/chevron-right.svg" alt="Expand Card" />
                                {/if}
                            </button>

                            {#if survey.active}
                                <div transition:slide|local class="card-body card-columns-container">
                                    <div class="card-columns">
                                        {#each survey.surveyAnswers as surveyAnswers}
                                            <div class="card-column">
                                                {#each surveyAnswers as surveyAnswer}
                                                    {#if surveyAnswer.immediateTargeted}
                                                        <div class="well" id="immediate-target-{survey.surveyHeaderUrn}-{surveyAnswer.questionNumber}">
                                                            <div class="well-icon well-icon-danger">
                                                                <img src="/assets/target-indicator-inverse.svg" alt="Immediate Targeting" />
                                                            </div>
                                                            <div class="card-column-row" class:selected-row="{selectedAnswerGroup && surveyAnswer.grouping == selectedAnswerGroup}"
                                                                    class:hidden-row="{selectedAnswerGroup && surveyAnswer.grouping != selectedAnswerGroup}">
                                                                <span>{getReferenceDataSurveyQuestionDescription(survey.surveyUrn, surveyAnswer.questionNumber)}</span>
                                                                <span style="color:{getReferenceDataSurveyAnswerColour(survey.surveyUrn, surveyAnswer.questionNumber, surveyAnswer.choiceUrn)}">{getReferenceDataSurveyAnswerDescription(survey.surveyUrn, surveyAnswer.questionNumber, surveyAnswer.choiceUrn)}</span>
                                                            </div>
                                                        </div>
                                                        <Tooltip target="immediate-target-{survey.surveyHeaderUrn}-{surveyAnswer.questionNumber}" placement="bottom">
                                                            {todaysConfig.inmateLabelName} was targeted immediately based on the response to this question
                                                        </Tooltip>
                                                    {:else}
                                                        <div class="card-column-row" class:selected-row="{selectedAnswerGroup && surveyAnswer.grouping == selectedAnswerGroup}"
                                                                class:hidden-row="{selectedAnswerGroup && surveyAnswer.grouping != selectedAnswerGroup}">
                                                            <span>{getReferenceDataSurveyQuestionDescription(survey.surveyUrn, surveyAnswer.questionNumber)}</span>
                                                            <span style="color:{getReferenceDataSurveyAnswerColour(survey.surveyUrn, surveyAnswer.questionNumber, surveyAnswer.choiceUrn)}">{getReferenceDataSurveyAnswerDescription(survey.surveyUrn, surveyAnswer.questionNumber, surveyAnswer.choiceUrn)}</span>
                                                        </div>
                                                    {/if}
                                                {/each}
                                            </div>
                                        {/each}
                                    </div>

                                    <div class="survey-details">
                                        <div class="survey-detail">
                                            {#if todaysConfig.showSiteName && survey.siteUrn}
                                                {getReferenceDataSiteName(survey.siteUrn)} &gt;
                                            {/if}

                                            {#if survey.unit || survey.wing || survey.cell}
                                                {#if survey.unit}{survey.unit}{/if}
                                                {#if survey.unit && survey.wing}&nbsp;&middot;&nbsp;{/if}
                                                {#if survey.wing}{survey.wing}{/if}
                                                {#if (survey.unit || survey.wing) && survey.cell}&nbsp;&middot;&nbsp;{/if}
                                                {#if survey.cell}{survey.cell}{/if}
                                            {:else}
                                                Location Not Recorded
                                            {/if}

                                            {#if survey.vptCellStatusUrn > 0}
                                                <img id="survey-detail-cell-status-indicator-{survey.surveyHeaderUrn}" src="/assets/{getReferenceDataItemFromList(survey.vptCellStatusUrn, $referenceDataCellStatusList, 'iconName', 'vptCellStatusUrn')}.svg" alt="{getReferenceDataItemFromList(survey.vptCellStatusUrn, $referenceDataCellStatusList, 'description', 'vptCellStatusUrn')}" />
                                                <Tooltip target="survey-detail-cell-status-indicator-{survey.surveyHeaderUrn}" placement="bottom">
                                                    {getReferenceDataItemFromList(survey.vptCellStatusUrn, $referenceDataCellStatusList, 'description', 'vptCellStatusUrn')}
                                                </Tooltip>
                                            {/if}
                                        </div>
                                        <div class="survey-detail">
                                            {#if survey.regimeLevel}
                                                {getReferenceDataItemFromList(survey.regimeLevel, $referenceDataRegimeLevelList, 'description', 'level')} Regime (Since {formatDate(survey.regimeStartDate)})
                                            {:else}
                                                Regime Not Recorded
                                            {/if}
                                        </div>
                                        <div class="survey-detail">
                                            {#if survey.keyworkerName}
                                                <span id="survey-detail-keyworker-{survey.surveyHeaderUrn}">{survey.keyworkerName} (Since {formatDate(survey.keyworkerStartDate)})</span>
                                                <Tooltip target="survey-detail-keyworker-{survey.surveyHeaderUrn}" placement="bottom">{todaysConfig.keyworkerLabelName}</Tooltip>
                                            {:else}
                                                {todaysConfig.keyworkerLabelName} Not Recorded
                                            {/if}
                                        </div>
                                    </div>
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
            {/each}
        {:else}
            <div class="alert alert-danger" role="alert">No wellbeing surveys have been completed.</div>
        {/if}
    {:else}
        <div class="spinner-container">
            <img id="aim-icon" src="/assets/logo-aim-target.png" alt="AIM Logo" />
            <img id="aim-spinner" src="/assets/logo-radar-spinner.png" alt="AIM Logo" />
        </div>
    {/if}
</div>